import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import Box from '@components/base/Box'
import Modal from '@components/base/Modal'
import Text from '@components/base/Text'
import { Button } from '@components/inputs/Button'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { TextInput } from '@components/inputs/TextInput'
import {
  OrganisationOrganiserRole,
  type User,
} from '@graphql/__generated/graphql'
import {
  CANCEL_ORGANISATION_ORGANISER_INVITE,
  INVITE_ORGANISATION_ORGANISER,
  REMOVE_ORGANISATION_ORGANISER,
  UPDATE_ORGANISATION_ORGANISER_ROLE,
} from '@graphql/users/manageOrganisationOrganisers'
import { useAuth } from '@libs/Auth/useAuth'
import { useTheme } from '@libs/useTheme'
import { v4 } from 'uuid'

type OrganiserRole = OrganisationOrganiserRole

type OrganiserUser = Omit<User, 'role'> & {
  role?: OrganiserRole | null
}

type PendingOrganiserInvite = {
  inviteId: string
  organisationName?: string | null
  email: string
  name?: string | null
  role?: OrganiserRole | null
  status?: string | null
  createdAt?: string | null
  expiresAt?: string | null
}

type OrganiserRow = OrganiserUser & {
  pending?: false
}

type PendingInviteRow = PendingOrganiserInvite & {
  pending: true
}

type TableOrganiserRow = OrganiserRow | PendingInviteRow

const systemEmailMarkers = ['forumm.to', 'pauljohnpirie', '448.studio']

const isSystemEmail = (email?: string | null) => {
  const lowerEmail = email?.toLowerCase() ?? ''
  return systemEmailMarkers.some((marker) => lowerEmail.includes(marker))
}

const hasGlobalOrganisationAccess = (permissions?: string[] | null) =>
  permissions?.some((permission) => {
    const [type, action, resource] = permission.split('::')
    return (
      type === 'USER' &&
      (action === 'ACCESS_ORGANISATION' || action === 'MANAGE_ORGANISATION') &&
      resource === '*'
    )
  }) ?? false

const getFiltered = (
  users: (TableOrganiserRow | null | undefined)[],
  filter: string,
  showSystemEmails: boolean
) => {
  const visibleUsers = (users || []).filter(
    (user): user is TableOrganiserRow =>
      !!user && (showSystemEmails || !isSystemEmail(user.email))
  )

  if (!filter) return visibleUsers

  const lowercasedSearchText = filter.toLowerCase()
  return visibleUsers.filter(
    (user) =>
      user?.name?.toLowerCase().includes(lowercasedSearchText) ||
      user?.email?.toLowerCase().includes(lowercasedSearchText)
  )
}

const getRoleLabel = (role: string | null | undefined) =>
  role === OrganisationOrganiserRole.SpaceAdmin ? 'Space Admin' : 'Organiser'

const getRoleFromPermissions = (
  permissions: string[] | null | undefined,
  organisationName: string
): OrganiserRole => {
  const isSpaceAdmin = permissions?.some((permission) => {
    const [type, action, resource] = permission.split('::')
    return (
      type === 'USER' &&
      action === 'MANAGE_ORGANISATION' &&
      (resource === '*' ||
        resource?.toUpperCase() === organisationName.toUpperCase())
    )
  })

  return isSpaceAdmin
    ? OrganisationOrganiserRole.SpaceAdmin
    : OrganisationOrganiserRole.Organiser
}

const getInviteName = (name: string) => {
  const parts = name.trim().split(' ').filter(Boolean)
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
  }
}

const TableOrganisers = ({
  users,
  pendingInvites = [],
  className,
  organisationName,
  refetch,
}: {
  users: OrganiserUser[]
  pendingInvites?: PendingOrganiserInvite[]
  className?: string
  organisationName: string
  refetch: () => void | Promise<unknown>
}) => {
  const { profile, hasPermission, isAdmin } = useAuth()
  const { theme } = useTheme()
  const isDarkTheme = theme.type === 'DARK'

  const org = organisationName || profile?.company || profile?.university || ''
  const canManageOrganisers =
    isAdmin || hasPermission(`USER::MANAGE_ORGANISATION::${org}`)

  const [filter, setFilter] = useState<string>('')
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState<OrganiserRole>(
    OrganisationOrganiserRole.Organiser
  )
  const [formError, setFormError] = useState<string>()
  const [statusMessage, setStatusMessage] = useState<string>()
  const [statusType, setStatusType] = useState<'success' | 'error'>('success')
  const [actionKey, setActionKey] = useState<string>()
  const [refreshLoading, setRefreshLoading] = useState(false)

  const [inviteOrganiser, { loading: inviteLoading }] = useMutation(
    INVITE_ORGANISATION_ORGANISER
  )
  const [removeOrganiser] = useMutation(REMOVE_ORGANISATION_ORGANISER)
  const [updateOrganiserRole] = useMutation(UPDATE_ORGANISATION_ORGANISER_ROLE)
  const [cancelInvite] = useMutation(CANCEL_ORGANISATION_ORGANISER_INVITE)

  const filteredData = useMemo(() => {
    const organiserRows = users.map((user) => ({
      ...user,
      pending: false as const,
      role:
        user.role ??
        getRoleFromPermissions(user.permissions ?? undefined, organisationName),
    }))
    const pendingRows = pendingInvites.map((invite) => ({
      ...invite,
      pending: true as const,
    }))

    return getFiltered(
      [...organiserRows, ...pendingRows],
      filter,
      !!isAdmin
    )?.sort((a, b) =>
      (a?.name ?? a?.email ?? '').localeCompare(b?.name ?? b?.email ?? '')
    )
  }, [users, pendingInvites, filter, organisationName, isAdmin])

  const resetInviteForm = () => {
    setInviteEmail('')
    setInviteName('')
    setInviteRole(OrganisationOrganiserRole.Organiser)
    setFormError(undefined)
  }

  const setStatus = (
    message: string,
    type: 'success' | 'error' = 'success'
  ) => {
    setStatusMessage(message)
    setStatusType(type)
  }

  const handleInviteSubmit = async () => {
    setFormError(undefined)
    setStatusMessage(undefined)

    const email = inviteEmail.trim().toLowerCase()
    const name = inviteName.trim()

    if (!email) {
      setFormError('Email address is required')
      return
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setFormError('Enter a valid email address')
      return
    }

    try {
      await inviteOrganiser({
        variables: {
          input: {
            organisationName,
            email,
            name,
            role: inviteRole,
            ...getInviteName(name),
          },
        },
      })
      await refetch()
      resetInviteForm()
      setInviteModalOpen(false)
      setStatus('Organiser invite sent')
    } catch (error) {
      setFormError('Unable to invite organiser')
    }
    setInviteModalOpen(false)
  }

  const handleRemoveOrganiser = async (row: OrganiserUser) => {
    if (!row.userId) return

    setActionKey(`remove-${row.userId}`)
    setStatusMessage(undefined)

    try {
      await removeOrganiser({
        variables: {
          input: {
            organisationName,
            userId: row.userId,
          },
        },
      })
      await refetch()
      setStatus('Organiser removed')
    } catch (error) {
      setStatus('Unable to remove organiser', 'error')
    } finally {
      setActionKey(undefined)
    }
  }

  const handleUpdateRole = async (row: OrganiserUser, role: OrganiserRole) => {
    if (!row.userId) return

    setActionKey(`role-${row.userId}`)
    setStatusMessage(undefined)

    try {
      await updateOrganiserRole({
        variables: {
          input: {
            organisationName,
            userId: row.userId,
            role,
          },
        },
      })
      await refetch()
      setStatus(
        role === OrganisationOrganiserRole.SpaceAdmin
          ? 'Organiser upgraded to Space Admin'
          : 'Space Admin changed to Organiser'
      )
    } catch (error) {
      setStatus('Unable to update organiser role', 'error')
    } finally {
      setActionKey(undefined)
    }
  }

  const handleCancelInvite = async (invite: PendingOrganiserInvite) => {
    if (!invite.inviteId) return

    setActionKey(`invite-${invite.inviteId}`)
    setStatusMessage(undefined)

    try {
      await cancelInvite({
        variables: {
          inviteId: invite.inviteId,
        },
      })
      await refetch()
      setStatus('Invite cancelled')
    } catch (error) {
      setStatus('Unable to cancel invite', 'error')
    } finally {
      setActionKey(undefined)
    }
  }

  const handleRefresh = async () => {
    setRefreshLoading(true)
    setStatusMessage(undefined)

    try {
      await refetch()
      setStatus('Organiser list refreshed')
    } catch (error) {
      setStatus('Unable to refresh organisers', 'error')
    } finally {
      setRefreshLoading(false)
    }
  }

  return (
    <>
      <Modal
        title="Invite organiser"
        show={inviteModalOpen}
        setShow={(show) => {
          setInviteModalOpen(show)
          if (!show) resetInviteForm()
        }}
      >
        <Box className="flex flex-col gap-4">
          <TextInput
            label="Name"
            value={inviteName}
            placeholder="Jane Smith"
            onChange={setInviteName}
            className="w-full"
          />
          <TextInput
            label="Email"
            value={inviteEmail}
            placeholder="jane@example.com"
            onChange={setInviteEmail}
            className="w-full"
            type="email"
          />
          <DropdownInput
            label="Role"
            value={inviteRole}
            onChange={(value) => setInviteRole(value as OrganiserRole)}
            options={[
              {
                label: 'Organiser',
                value: OrganisationOrganiserRole.Organiser,
              },
              {
                label: 'Space Admin',
                value: OrganisationOrganiserRole.SpaceAdmin,
              },
            ]}
            className="w-full"
          />
          {formError && (
            <Box className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-center text-sm font-semibold text-red-700">
              {formError}
            </Box>
          )}
          <Box className="flex w-full flex-col gap-3 sm:flex-row">
            <Button
              title="Cancel"
              type="secondary"
              className="flex-1"
              onClick={() => {
                setInviteModalOpen(false)
                resetInviteForm()
              }}
            />
            <Button
              title="Send invite"
              className="flex-1"
              loading={inviteLoading}
              disabled={inviteLoading}
              onClick={handleInviteSubmit}
            />
          </Box>
        </Box>
      </Modal>
      <Box className="w-full flex justify-center items-center flex-col max-w-[1500px]">
        <Box className="w-full flex flex-col justify-end gap-3 mb-4 pr-5 sm:flex-row sm:items-center">
          <Button
            title="Refresh"
            type="secondary"
            size="auto"
            className="w-full md:w-auto"
            loading={refreshLoading}
            disabled={refreshLoading}
            onClick={handleRefresh}
          />
          {canManageOrganisers && (
            <Button
              title="Invite organiser"
              size="auto"
              className="w-full md:w-auto"
              onClick={() => setInviteModalOpen(true)}
            />
          )}
        </Box>
        <Box
          color="foregroundColour"
          className={`w-full flex-2 rounded-3xl border shadow-md border-forumm-menu-border p-3 sm:p-5 max-w-[1500px] justify-center items-center ${className}`}
        >
          <Box className="flex flex-col gap-4 md:flex-row w-full justify-between items-center mb-8">
            <Text className="text-xl text-center md:text-left">{`${org}'s Organiser Account${
              users?.length > 1 ? 's' : ''
            }`}</Text>
            <Box className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
              {/* {canManageOrganisers && ( */}
              {/*   <Button */}
              {/*     title="Invite organiser" */}
              {/*     size="auto" */}
              {/*     className="w-full md:w-auto" */}
              {/*     onClick={() => setInviteModalOpen(true)} */}
              {/*   /> */}
              {/* )} */}
              <Box className="md:min-w-[300px]">
                <TextInput
                  value={filter}
                  className="w-full"
                  placeholder="Enter your search terms"
                  onChange={setFilter}
                />
              </Box>
            </Box>
          </Box>
          {statusMessage && (
            <Box
              className={`mb-4 rounded-xl border px-4 py-3 text-center text-sm font-semibold ${
                statusType === 'success'
                  ? 'border-green-300 bg-green-50 text-green-700'
                  : 'border-red-300 bg-red-50 text-red-700'
              }`}
            >
              {statusMessage}
            </Box>
          )}
          <Box className="max-h-[360px] overflow-x-auto overflow-y-scroll scrollbar-hide">
            <Box
              className={`w-full min-w-[920px] flex flex-row ${
                isDarkTheme ? 'bg-dark' : 'bg-forumm-light-gray'
              } rounded-t-xl py-2 mb-5`}
            >
              <Box className="flex-1 font-bold text-start text-sm sm:text-base ml-4">
                Organiser
              </Box>
              <Box className="flex-1 font-bold text-sm md:text-base">Email</Box>
              <Box className="flex-1 font-bold text-sm md:text-base">
                Position
              </Box>
              <Box className="flex-1 font-bold text-sm md:text-base">
                Organisation
              </Box>
              <Box className="w-[130px] font-bold text-sm md:text-base">
                Status
              </Box>
              {canManageOrganisers && (
                <Box className="w-[260px] font-bold text-sm md:text-base">
                  Actions
                </Box>
              )}
            </Box>
            {(filteredData || []).map((row) => {
              const isPending = row.pending === true
              const pendingRow = row as PendingInviteRow
              const organiserRow = row as OrganiserRow
              const role = isPending
                ? pendingRow.role
                : ((organiserRow.role ??
                    getRoleFromPermissions(
                      organiserRow.permissions ?? undefined,
                      organisationName
                    )) as OrganiserRole)
              const isCurrentUser =
                !isPending && organiserRow.userId === profile?.userId
              const rowKey = isPending
                ? pendingRow.inviteId
                : organiserRow.email || organiserRow.userId || v4()
              const showSystemEmailHint = !!isAdmin && isSystemEmail(row.email)
              const hasGlobalAccess =
                !isPending &&
                hasGlobalOrganisationAccess(organiserRow.permissions)

              return (
                <Box
                  key={rowKey}
                  className="flex min-w-[920px] flex-row items-center mb-4"
                >
                  <Box className="flex-1 flex flex-row text-start justify-start text-sm md:text-base ml-4 break-all p-2">
                    {!isPending && (
                      <Box
                        className="w-[30px] h-[30px] shrink-0 rounded-full translate-y-[-4px] mr-2 bg-cover"
                        style={{
                          backgroundImage: `url(${
                            organiserRow.profileImage
                              ? organiserRow.profileImage
                              : 'https://assets.tumblr.com/images/default_header/optica_pattern_11.png'
                          })`,
                        }}
                      />
                    )}{' '}
                    <Box className="flex min-w-0 flex-col gap-1">
                      <Box>
                        {row.name || (isPending ? 'Pending organiser' : '')}
                      </Box>
                      {showSystemEmailHint && (
                        <Box className="text-xs font-semibold text-forumm-orange">
                          Only you can see this
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box className="flex-1 text-sm md:text-base break-all p-2">
                    {row.email}
                  </Box>
                  <Box className="flex-1 text-ellipsis text-sm md:text-base break-all p-2">
                    {isPending ? '-' : organiserRow.jobTitle}
                  </Box>
                  <Box className="flex-1 text-sm md:text-base">
                    {isPending
                      ? org
                      : (organiserRow.company ?? organiserRow.university)}
                  </Box>
                  <Box className="w-[130px] text-sm md:text-base">
                    {isPending ? 'Pending invite' : getRoleLabel(role)}
                  </Box>
                  {canManageOrganisers && (
                    <Box className="flex w-[260px] flex-row flex-wrap gap-2 text-sm md:text-base">
                      {isPending ? (
                        <Button
                          title="Cancel"
                          type="danger"
                          size="small"
                          loading={
                            actionKey === `invite-${pendingRow.inviteId}`
                          }
                          disabled={
                            actionKey === `invite-${pendingRow.inviteId}`
                          }
                          onClick={() => handleCancelInvite(pendingRow)}
                        />
                      ) : hasGlobalAccess ? (
                        <Box className="text-xs font-semibold text-forumm-orange">
                          Global access
                        </Box>
                      ) : (
                        <>
                          {role === OrganisationOrganiserRole.SpaceAdmin ? (
                            !isCurrentUser && (
                              <Button
                                title="Downgrade"
                                type="secondary"
                                size="small"
                                loading={
                                  actionKey === `role-${organiserRow.userId}`
                                }
                                disabled={
                                  actionKey === `role-${organiserRow.userId}`
                                }
                                onClick={() =>
                                  handleUpdateRole(
                                    organiserRow,
                                    OrganisationOrganiserRole.Organiser
                                  )
                                }
                              />
                            )
                          ) : (
                            <Button
                              title="Make admin"
                              type="secondary"
                              size="small"
                              loading={
                                actionKey === `role-${organiserRow.userId}`
                              }
                              disabled={
                                actionKey === `role-${organiserRow.userId}`
                              }
                              onClick={() =>
                                handleUpdateRole(
                                  organiserRow,
                                  OrganisationOrganiserRole.SpaceAdmin
                                )
                              }
                            />
                          )}
                          {!isCurrentUser && (
                            <Button
                              title="Remove"
                              type="danger"
                              size="small"
                              loading={
                                actionKey === `remove-${organiserRow.userId}`
                              }
                              disabled={
                                actionKey === `remove-${organiserRow.userId}`
                              }
                              confirmationModal={{
                                title: 'Remove organiser',
                                content:
                                  'This will remove organiser access for this organisation.',
                              }}
                              onClick={() =>
                                handleRemoveOrganiser(organiserRow)
                              }
                            />
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )
            })}
            {(filteredData || []).length < 1 && (
              <Box className="py-8 text-center text-sm">
                No organisers found.
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default TableOrganisers
